/* ------------------------------------------------------------------------------
 *
 *  # Student Observations Management Components
 *
 *  Grid de observaciones de estudiantes con timeline, filtros, CKEditor y DateRangePicker
 *
 * ---------------------------------------------------------------------------- */

import { 
	initGenericDataTable, 
	refreshDataTable, 
	destroyDataTable 
} from '../integrations/datatables.js';

// Variables de estado
let studentObservationsCallbacks = null;
let observacionEditorInstance = null;
let dateRangePickerInstance = null;

// Helper functions
function getCategoryBadgeClass(categoria) {
	switch(categoria) {
		case 'Académico': return 'bg-primary';
		case 'Social': return 'bg-success';
		case 'Emocional': return 'bg-warning';
		case 'Físico': return 'bg-info';
		default: return 'bg-secondary';
	}
}

function getCategoryIcon(categoria) {
	const icons = {
		'Académico': 'ph-graduation-cap',
		'Social': 'ph-users-three',
		'Emocional': 'ph-heart',
		'Físico': 'ph-activity'
	};
	return icons[categoria] || 'ph-note';
}

function getVisibilityIcon(isPublic) {
	return isPublic ? 'ph-eye' : 'ph-eye-slash';
}

function getVisibilityText(isPublic) {
	return isPublic ? 'Visible para padres' : 'Solo guías';
}

// CKEditor initialization
export function initCKEditor(elementId, initialContent = '', dotNetRef = null) {
	const editorElement = document.querySelector(`#${elementId} .document-editor-editable`);
	if (!editorElement) {
		console.warn(`CKEditor element #${elementId} .document-editor-editable not found`);
		return null;
	}

	// Destroy previous instance if exists
	if (observacionEditorInstance) {
		observacionEditorInstance.destroy().then(() => {
			observacionEditorInstance = null;
		});
	}

	DecoupledEditor.create(editorElement, {
		heading: {
			options: [
				{ model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
				{ model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
				{ model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
				{ model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' }
			]
		},
		placeholder: 'Escribe la observación aquí...'
	})
	.then(editor => {
		observacionEditorInstance = editor;
		
		// Attach toolbar
		const toolbarContainer = document.querySelector(`#${elementId} .document-editor-toolbar`);
		if (toolbarContainer) {
			toolbarContainer.appendChild(editor.ui.view.toolbar.element);
		}

		// Set initial content
		if (initialContent) {
			editor.setData(initialContent);
		}

		// Change callback
		if (dotNetRef) {
			editor.model.document.on('change:data', () => {
				const data = editor.getData();
				dotNetRef.invokeMethodAsync('OnEditorContentChanged', data);
			});
		}

		console.log('CKEditor initialized successfully');
		return editor;
	})
	.catch(error => {
		console.error('CKEditor initialization error:', error);
		return null;
	});
}

// Get CKEditor content
export function getCKEditorContent() {
	if (observacionEditorInstance) {
		return observacionEditorInstance.getData();
	}
	return '';
}

// Set CKEditor content
export function setCKEditorContent(content) {
	if (observacionEditorInstance) {
		observacionEditorInstance.setData(content || '');
	}
}

// Destroy CKEditor
export function destroyCKEditor() {
	if (observacionEditorInstance) {
		observacionEditorInstance.destroy().then(() => {
			observacionEditorInstance = null;
			console.log('CKEditor destroyed');
		});
	}
}

// DateRangePicker trimestral initialization
export function initDateRangePicker(inputId, dotNetRef = null) {
	const inputElement = $(`#${inputId}`);
	if (!inputElement.length) {
		console.warn(`DateRangePicker input #${inputId} not found`);
		return null;
	}

	// Destroy previous instance
	if (dateRangePickerInstance) {
		inputElement.data('daterangepicker')?.remove();
		dateRangePickerInstance = null;
	}

	// Calcular trimestres del año actual
	const currentYear = new Date().getFullYear();
	const quarters = {
		[`Q1 ${currentYear} (Ene-Mar)`]: [
			moment(`${currentYear}-01-01`), 
			moment(`${currentYear}-03-31`)
		],
		[`Q2 ${currentYear} (Abr-Jun)`]: [
			moment(`${currentYear}-04-01`), 
			moment(`${currentYear}-06-30`)
		],
		[`Q3 ${currentYear} (Jul-Sep)`]: [
			moment(`${currentYear}-07-01`), 
			moment(`${currentYear}-09-30`)
		],
		[`Q4 ${currentYear} (Oct-Dic)`]: [
			moment(`${currentYear}-10-01`), 
			moment(`${currentYear}-12-31`)
		]
	};

	const options = {
		startDate: moment().startOf('quarter'),
		endDate: moment().endOf('quarter'),
		parentEl: '.content-inner',
		ranges: quarters,
		locale: {
			format: 'DD/MM/YYYY',
			separator: ' - ',
			applyLabel: 'Aplicar',
			cancelLabel: 'Cancelar',
			fromLabel: 'Desde',
			toLabel: 'Hasta',
			customRangeLabel: 'Rango personalizado',
			weekLabel: 'S',
			daysOfWeek: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
			monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
						 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
			firstDay: 1
		}
	};

	inputElement.daterangepicker(options, function(start, end, label) {
		console.log('Date range selected:', start.format('YYYY-MM-DD'), 'to', end.format('YYYY-MM-DD'));
		
		if (dotNetRef) {
			dotNetRef.invokeMethodAsync('OnDateRangeChanged', 
				start.format('YYYY-MM-DD'), 
				end.format('YYYY-MM-DD')
			);
		}
	});

	dateRangePickerInstance = inputElement.data('daterangepicker');
	
	// Display initial value
	inputElement.val(
		moment().startOf('quarter').format('DD/MM/YYYY') + ' - ' + 
		moment().endOf('quarter').format('DD/MM/YYYY')
	);

	return dateRangePickerInstance;
}

// Get current date range
export function getCurrentDateRange() {
	if (dateRangePickerInstance) {
		return {
			startDate: dateRangePickerInstance.startDate.format('YYYY-MM-DD'),
			endDate: dateRangePickerInstance.endDate.format('YYYY-MM-DD')
		};
	}
	return null;
}

// Función principal de inicialización del grid
export function initStudentObservationsGrid(observaciones) {
	destroyDataTable('studentObservationsTable');
	
	const tableData = observaciones.map(obs => [
		generateObservationActions(obs.id, obs.publica),
		generateObservationColumn(obs),
		generateCategoryColumn(obs),
		generateAuthorColumn(obs),
		obs.fecha ? new Date(obs.fecha).toLocaleDateString('es-ES') : '-',
		generateVisibilityColumn(obs)
	]);
	
	const options = {
		data: tableData,
		columns: [
			{ title: 'Acciones', orderable: false, className: 'text-center no-export', width: '100px' },
			{ title: 'Observación', width: '40%' },
			{ title: 'Categoría', width: '15%' },
			{ title: 'Autor', width: '15%' },
			{ title: 'Fecha', className: 'text-center', width: '15%' },
			{ title: 'Visibilidad', className: 'text-center', width: '15%' }
		],
		exportTitle: 'Observaciones de Estudiante - Montessori Nova',
		order: [[4, 'desc']], // Orden por fecha descendente
		buttons: [
			{
				extend: 'collection',
				className: 'btn btn-flat-primary btn-labeled btn-labeled-start dropdown-toggle me-3 rounded',
				text: '<span class="btn-labeled-icon bg-primary text-white rounded-start"><i class="ph-export"></i></span>Exportar',
				buttons: [
					{
						extend: 'excel',
						text: '<i class="ph-file-xls me-1"></i>Excel',
						className: 'dropdown-item',
						title: 'Observaciones de Estudiante - Montessori Nova',
						exportOptions: {
							columns: [1, 2, 3, 4, 5],
							format: {
								body: function (inner, coldex, rowdex) {
									if (!inner || (typeof inner === 'string' && !inner.includes('<'))) {
										return inner;
									}
									const $el = $(inner);
									const exportText = $el.attr('data-export');
									if (exportText) {
										return exportText;
									}
									if (inner.length <= 0) return inner;
									let el = $.parseHTML(inner);
									let result = '';
									$.each(el, function (index, item) {
										if (item.innerText === undefined) {
											result = result + item.textContent;
										} else {
											result = result + item.innerText;
										}
									});
									return result;
								}
							}
						}
					},
					{
						extend: 'copy',
						text: '<i class="ph-clipboard-text me-1"></i>Copiar',
						className: 'dropdown-item',
						exportOptions: {
							columns: [1, 2, 3, 4, 5],
							format: {
								body: function (inner, coldex, rowdex) {
									if (!inner || (typeof inner === 'string' && !inner.includes('<'))) {
										return inner;
									}
									const $el = $(inner);
									const exportText = $el.attr('data-export');
									if (exportText) {
										return exportText;
									}
									if (inner.length <= 0) return inner;
									let el = $.parseHTML(inner);
									let result = '';
									$.each(el, function (index, item) {
										if (item.innerText === undefined) {
											result = result + item.textContent;
										} else {
											result = result + item.innerText;
										}
									});
									return result;
								}
							}
						}
					}
				]
			}
		]
	};
	
	return initGenericDataTable('studentObservationsTable', options);
}

function generateObservationActions(observacionId, isPublic) {
	const visibilityIcon = getVisibilityIcon(isPublic);
	const visibilityTitle = isPublic ? 'Ocultar de padres' : 'Hacer visible para padres';
	
	return `
		<div class="d-inline-flex">
			<a href="javascript:void(0)" class="text-body me-2" 
			   onclick="window.sfBlazorSB.students.observations.edit(${observacionId})" 
			   title="Editar observación">
				<i class="ph-pencil"></i>
			</a>
			<a href="javascript:void(0)" class="text-body me-2" 
			   onclick="window.sfBlazorSB.students.observations.toggleVisibility(${observacionId}, ${isPublic})" 
			   title="${visibilityTitle}">
				<i class="${visibilityIcon}"></i>
			</a>
			<a href="javascript:void(0)" class="text-body" 
			   onclick="window.sfBlazorSB.students.observations.deleteObservation(${observacionId})" 
			   title="Eliminar observación">
				<i class="ph-trash"></i>
			</a>
		</div>
	`;
}

function generateObservationColumn(observacion) {
	const icon = getCategoryIcon(observacion.categoriaNombre || '');
	const contenido = observacion.contenido || 'Sin contenido';
	// Strip HTML for preview
	const tempDiv = document.createElement('div');
	tempDiv.innerHTML = contenido;
	const textContent = tempDiv.textContent || tempDiv.innerText || '';
	const preview = textContent.length > 150 ? textContent.substring(0, 150) + '...' : textContent;
	const exportText = textContent;
	
	return `
		<div class="d-flex align-items-start" data-export="${exportText.replace(/"/g, '&quot;')}">
			<i class="${icon} fs-3 me-2 text-primary"></i>
			<div>
				<div class="fw-semibold">${preview}</div>
				${observacion.actividadNombre ? `<div class="text-muted fs-sm mt-1"><i class="ph-link ph-xs me-1"></i>${observacion.actividadNombre}</div>` : ''}
			</div>
		</div>
	`;
}

function generateCategoryColumn(observacion) {
	const categoria = observacion.categoriaNombre || 'General';
	const badgeClass = getCategoryBadgeClass(categoria);
	return `<span class="badge ${badgeClass}" data-export="${categoria}">${categoria}</span>`;
}

function generateAuthorColumn(observacion) {
	const autor = observacion.usuarioNombre || 'Desconocido';
	return `
		<div data-export="${autor}">
			<div class="fw-semibold">${autor}</div>
		</div>
	`;
}

function generateVisibilityColumn(observacion) {
	const visibilityText = getVisibilityText(observacion.publica);
	const icon = getVisibilityIcon(observacion.publica);
	const badgeClass = observacion.publica ? 'bg-success' : 'bg-secondary';
	
	return `
		<span class="badge ${badgeClass}" data-export="${visibilityText}">
			<i class="${icon} me-1"></i>${visibilityText}
		</span>
	`;
}

// Refresh
export function refreshStudentObservationsGrid(observaciones) {
	destroyDataTable('studentObservationsTable');
	initStudentObservationsGrid(observaciones);
}

// Callbacks
export function setStudentObservationsCallbacks(dotNetRef) {
	studentObservationsCallbacks = dotNetRef;
}

// Acciones
export function edit(observacionId) {
	if (studentObservationsCallbacks) {
		studentObservationsCallbacks.invokeMethodAsync('EditObservation', observacionId);
	}
}

export async function toggleVisibility(observacionId, isPublic) {
	const action = isPublic ? 'ocultar' : 'hacer visible';
	const title = isPublic ? 'Ocultar Observación' : 'Hacer Visible';
	const text = `¿Deseas ${action} esta observación para los padres?`;
	
	const result = await window.swalInit.fire({
		title: title,
		text: text,
		icon: 'question',
		showCancelButton: true,
		confirmButtonText: 'Sí, continuar',
		cancelButtonText: 'Cancelar'
	});
	
	if (result.isConfirmed && studentObservationsCallbacks) {
		await studentObservationsCallbacks.invokeMethodAsync('ToggleVisibility', observacionId, isPublic);
	}
}

export async function deleteObservation(observacionId) {
	const result = await window.swalInit.fire({
		title: 'Eliminar Observación',
		text: '¿Estás seguro de que deseas eliminar esta observación? Esta acción no se puede deshacer.',
		icon: 'warning',
		showCancelButton: true,
		confirmButtonText: 'Sí, eliminar',
		cancelButtonText: 'Cancelar',
		confirmButtonColor: '#dc3545'
	});
	
	if (result.isConfirmed && studentObservationsCallbacks) {
		await studentObservationsCallbacks.invokeMethodAsync('DeleteObservation', observacionId);
	}
}

// Filtros
export function applyObservationFilters() {
	const categoriaFilter = document.getElementById('categoriaFilter')?.value || '';
	const fechaDesde = document.getElementById('fechaDesde')?.value || '';
	const fechaHasta = document.getElementById('fechaHasta')?.value || '';
	const globalSearch = document.getElementById('globalSearch')?.value || '';
	
	const table = $('#studentObservationsTable').DataTable();
	
	// Clear previous searches
	$.fn.dataTable.ext.search.splice(0);
	
	// Add custom searches
	if (categoriaFilter) {
		$.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
			return data[2].includes(categoriaFilter);
		});
	}
	
	if (fechaDesde || fechaHasta) {
		$.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
			const fechaStr = data[4]; // Columna 4 = Fecha
			if (!fechaStr || fechaStr === '-') return true;
			
			const [day, month, year] = fechaStr.split('/');
			const fechaObservacion = new Date(year, month - 1, day);
			
			if (fechaDesde) {
				const desde = new Date(fechaDesde);
				if (fechaObservacion < desde) return false;
			}
			
			if (fechaHasta) {
				const hasta = new Date(fechaHasta);
				if (fechaObservacion > hasta) return false;
			}
			
			return true;
		});
	}
	
	// Apply global search
	table.search(globalSearch).draw();
}

export function clearObservationFilters() {
	const categoriaFilter = document.getElementById('categoriaFilter');
	const fechaDesde = document.getElementById('fechaDesde');
	const fechaHasta = document.getElementById('fechaHasta');
	const globalSearch = document.getElementById('globalSearch');
	
	if (categoriaFilter) categoriaFilter.value = '';
	if (fechaDesde) fechaDesde.value = '';
	if (fechaHasta) fechaHasta.value = '';
	if (globalSearch) globalSearch.value = '';
	
	const table = $('#studentObservationsTable').DataTable();
	$.fn.dataTable.ext.search.splice(0);
	table.search('').draw();
}

// DateRangePicker para Modal de Observaciones
export function initObservacionDateRangePicker(inputId, dotNetRef = null) {
	if (!window.jQuery || !$.fn.daterangepicker) {
		console.warn('DateRangePicker plugin not loaded');
		return null;
	}

	const $input = $(`#${inputId}`);
	if (!$input.length) {
		console.warn('Input element not found:', inputId);
		return null;
	}

	// Calcular rangos predefinidos
	const currentYear = new Date().getFullYear();
	const today = moment();

	const ranges = {
		// Trimestral (Taller)
		[`Q1 ${currentYear} (Ene-Mar)`]: [
			moment([currentYear, 0, 1]),
			moment([currentYear, 2, 31])
		],
		[`Q2 ${currentYear} (Abr-Jun)`]: [
			moment([currentYear, 3, 1]),
			moment([currentYear, 5, 30])
		],
		[`Q3 ${currentYear} (Jul-Sep)`]: [
			moment([currentYear, 6, 1]),
			moment([currentYear, 8, 30])
		],
		[`Q4 ${currentYear} (Oct-Dic)`]: [
			moment([currentYear, 9, 1]),
			moment([currentYear, 11, 31])
		],
		// Mensual (Casa de Niños)
		'Este mes': [moment().startOf('month'), moment().endOf('month')],
		'Mes pasado': [
			moment().subtract(1, 'month').startOf('month'),
			moment().subtract(1, 'month').endOf('month')
		],
		// Esporádico (Nido/Comunidad)
		'Últimos 7 días': [moment().subtract(6, 'days'), today],
		'Últimos 30 días': [moment().subtract(29, 'days'), today]
	};

	// NO establecer fechas por defecto - obligar al usuario a seleccionar
	$input.daterangepicker({
		autoUpdateInput: false, // ⭐ NO actualizar automáticamente
		ranges: ranges,
		locale: {
			format: 'DD/MM/YYYY',
			separator: ' - ',
			applyLabel: 'Aplicar',
			cancelLabel: 'Cancelar',
			fromLabel: 'Desde',
			toLabel: 'Hasta',
			customRangeLabel: 'Rango personalizado',
			weekLabel: 'S',
			daysOfWeek: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
			monthNames: [
				'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
				'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
			],
			firstDay: 1
		},
		opens: 'center',
		drops: 'auto'
	});

	// ⭐ Actualizar input solo cuando se aplica la selección
	$input.on('apply.daterangepicker', function(ev, picker) {
		$(this).val(picker.startDate.format('DD/MM/YYYY') + ' - ' + picker.endDate.format('DD/MM/YYYY'));
		
		console.log('DateRange selected:', picker.startDate.format('YYYY-MM-DD'), 'to', picker.endDate.format('YYYY-MM-DD'));
		
		if (dotNetRef) {
			dotNetRef.invokeMethodAsync('OnDateRangeSelected', 
				picker.startDate.format('YYYY-MM-DD'), 
				picker.endDate.format('YYYY-MM-DD')
			);
		}
	});

	// ⭐ Limpiar input si se cancela
	$input.on('cancel.daterangepicker', function(ev, picker) {
		$(this).val('');
	});

	// NO establecer valor inicial - dejar el placeholder visible
	return $input.data('daterangepicker');
}
